import React from 'react';

// CSS Styles (include this in your CSS file or styled-components)
const loaderStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 80%, 100% { 
      transform: scale(0.6);
      opacity: 0.5;
    }
    40% { 
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @keyframes bars {
    0%, 40%, 100% { 
      height: 20%;
    }
    20% { 
      height: 100%;
    }
  }
  
  .loader-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.9);
    z-index: 1000;
    backdrop-filter: blur(2px);
  }
  
  .loader-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 9999;
    backdrop-filter: blur(3px);
  }
  
  .loader-paper {
    padding: 24px;
    border-radius: 8px;
    background-color: white;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    border: 1px solid #e0e0e0;
  }
  
  .loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }
  
  .loader-message {
    font-size: 14px;
    color: #666;
    text-align: center;
    margin: 0;
    font-family: 'Roboto', sans-serif;
  }
`;

// Color helper
const getColor = (colorProp) => {
  const colors = {
    primary: '#1976d2',
    secondary: '#dc004e',
    success: '#2e7d32',
    error: '#d32f2f',
    warning: '#ed6c02',
    info: '#0288d1'
  };
  return colors[colorProp] || colorProp;
};

// 1. SPIN LOADER (Circular)
export const SpinLoader = ({ 
  size = 40, 
  color = 'primary', 
  message = '', 
  overlay = false, 
  fullScreen = false,
  thickness = 4,
  sx = {} 
}) => {
  const resolvedColor = getColor(color);
  
  const spinnerStyle = {
    width: `${size}px`,
    height: `${size}px`,
    border: `${thickness}px solid #f3f3f3`,
    borderTop: `${thickness}px solid ${resolvedColor}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const LoaderContent = () => (
    <div className="loader-container" style={sx}>
      <div style={spinnerStyle} />
      {message && <p className="loader-message">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-fullscreen">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  if (overlay) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-overlay">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{loaderStyles}</style>
      <LoaderContent />
    </>
  );
};

// 2. DOTS LOADER (Pulsing dots)
export const DotsLoader = ({ 
  size = 40, 
  color = 'primary', 
  message = '', 
  overlay = false, 
  fullScreen = false,
  sx = {} 
}) => {
  const resolvedColor = getColor(color);
  
  const DotsSpinner = () => (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          style={{
            width: `${size / 4}px`,
            height: `${size / 4}px`,
            backgroundColor: resolvedColor,
            borderRadius: '50%',
            animation: `pulse 1.4s infinite ease-in-out both`,
            animationDelay: `${i * 0.16}s`
          }}
        />
      ))}
    </div>
  );

  const LoaderContent = () => (
    <div className="loader-container" style={sx}>
      <DotsSpinner />
      {message && <p className="loader-message">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-fullscreen">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  if (overlay) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-overlay">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{loaderStyles}</style>
      <LoaderContent />
    </>
  );
};

// 3. BARS LOADER (Animated bars)
export const BarsLoader = ({ 
  size = 40, 
  color = '#00c6ff', 
  message = 'Analyzing resumes...', 
  overlay = false, 
  fullScreen = false,
  sx = {} 
}) => {
  const BarsSpinner = () => (
    <div
      style={{
        display: 'flex',
        gap: '4px',
        alignItems: 'flex-end',
        height: `${size}px`,
      }}
    >
      {[0, 1, 2, 3, 4].map(i => (
        <div
          key={i}
          style={{
            width: `${size / 7}px`,
            height: '100%',
            backgroundColor: color,
            borderRadius: '4px',
            animation: `barBounce 1s infinite ease-in-out`,
            animationDelay: `${i * 0.15}s`
          }}
        />
      ))}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: fullScreen ? '100vh' : '100%',
        width: '100%',
        position: overlay ? 'fixed' : 'relative',
        top: 0,
        left: 0,
        background: overlay
          ? 'linear-gradient(135deg, rgba(30,30,47,0.95), rgba(44,62,80,0.95))'
          : 'transparent',
        zIndex: overlay ? 1300 : 'auto',
        ...sx
      }}
    >
      <BarsSpinner />
      {message && (
        <p
          style={{
            marginTop: '14px',
            color: color,
            fontSize: '1rem',
            fontWeight: 600,
            letterSpacing: '0.5px'
          }}
        >
          {message}
        </p>
      )}
      <style>
        {`
          @keyframes barBounce {
            0%, 40%, 100% {
              transform: scaleY(0.4);
            }
            20% {
              transform: scaleY(1);
            }
          }
        `}
      </style>
    </div>
  );
};

// 4. RING LOADER (Double ring)
export const RingLoader = ({ 
  size = 40, 
  color = 'primary', 
  message = '', 
  overlay = false, 
  fullScreen = false,
  thickness = 4,
  sx = {} 
}) => {
  const resolvedColor = getColor(color);
  
  const RingSpinner = () => (
    <div style={{ position: 'relative' }}>
      <div style={{
        width: `${size}px`,
        height: `${size}px`,
        border: `${thickness}px solid transparent`,
        borderTop: `${thickness}px solid ${resolvedColor}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <div style={{
        position: 'absolute',
        top: `${thickness}px`,
        left: `${thickness}px`,
        width: `${size - thickness * 4}px`,
        height: `${size - thickness * 4}px`,
        border: `${thickness}px solid transparent`,
        borderBottom: `${thickness}px solid ${resolvedColor}`,
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite reverse'
      }} />
    </div>
  );

  const LoaderContent = () => (
    <div className="loader-container" style={sx}>
      <RingSpinner />
      {message && <p className="loader-message">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-fullscreen">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  if (overlay) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-overlay">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{loaderStyles}</style>
      <LoaderContent />
    </>
  );
};

// 5. PULSE LOADER (Growing circle)
export const PulseLoader = ({ 
  size = 40, 
  color = 'primary', 
  message = '', 
  overlay = false, 
  fullScreen = false,
  sx = {} 
}) => {
  const resolvedColor = getColor(color);
  
  const PulseSpinner = () => (
    <div style={{
      width: `${size}px`,
      height: `${size}px`,
      backgroundColor: resolvedColor,
      borderRadius: '50%',
      animation: 'pulse 1.5s ease-in-out infinite'
    }} />
  );

  const LoaderContent = () => (
    <div className="loader-container" style={sx}>
      <PulseSpinner />
      {message && <p className="loader-message">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-fullscreen">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  if (overlay) {
    return (
      <>
        <style>{loaderStyles}</style>
        <div className="loader-overlay">
          <div className="loader-paper">
            <LoaderContent />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{loaderStyles}</style>
      <LoaderContent />
    </>
  );
};

export default SpinLoader;

